import Chip from "components/Chip";
import dayjs from "dayjs";
import { BookingRequestDetails, BookingRequestStatus } from "features/types";
import React, { useState } from "react";
import { ScrollView, Text, View } from "react-native";
import { formatEventDate } from "utils/eventUtils";

interface BookingRequestsProps {
  bookingRequests: BookingRequestDetails[];
}

const BookingRequest = ({
  bookingRequest,
}: {
  bookingRequest: BookingRequestDetails;
}) => {
  function getStatusConfig(status: BookingRequestStatus) {
    const configs = {
      PENDING: {
        class: "bg-yellow-100 text-yellow-800 border-yellow-200",
        text: "Pending Review",
        icon: "⏳",
        description: "Your request is being reviewed",
      },
      APPROVED: {
        class: "bg-green-100 text-green-800 border-green-200",
        text: "Approved",
        icon: "✅",
        description: "Congratulations! Your request was approved",
      },
      APPROVED_NOT_SENT: {
        class: "bg-green-100 text-green-800 border-green-200",
        text: "Pending Delivery",
        icon: "✅",
        description: "Your request was approved but not delivered yet",
      },
      WAITLISTED: {
        class: "bg-blue-100 text-blue-800 border-blue-200",
        text: "Waitlisted",
        icon: "⏳",
        description: "Your request is on the waitlist",
      },
      DECLINED: {
        class: "bg-red-100 text-red-800 border-red-200",
        text: "Not Approved",
        icon: "❌",
        description: "Unfortunately, your request was not approved",
      },
      CANCELLED: {
        class: "bg-gray-100 text-gray-800 border-gray-200",
        text: "Cancelled",
        icon: "⛔",
        description: "Your request was cancelled",
      },
    };
    return configs[status];
  }

  const {
    event: { name, venueName, venueCity, venueState },
    createdAt,
  } = bookingRequest;
  const statusConfig = getStatusConfig(bookingRequest.status);
  return (
    <View className="bg-white mb-4 rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
      <View className="mb-4">
        <Text className="font-semibold text-gray-900 text-lg mb-1">{name}</Text>
        <View className="mb-2 gap-3">
          <Text className="text-gray-600 text-md">
            {formatEventDate(bookingRequest.event)}
          </Text>
          <Text className="text-gray-600 text-md">
            {venueName} - {venueCity}, {venueState}
          </Text>
          <View
            className={`w-max my-3 inline-gap-2 px-3 py-1 rounded-full text-sm font-medium border ${statusConfig.class}`}
          >
            <Text>
              {statusConfig.icon} {statusConfig.text}
            </Text>
          </View>
          <View>
            <Text className="text-gray-600 text-sm">
              {statusConfig.description}
            </Text>
          </View>
        </View>
        <View className="flex flex-row items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
          <Text className="text-gray-600 text-sm">
            Tickets: {bookingRequest.quantity}
          </Text>
          <Text className="text-gray-600 text-sm">
            Requested: {dayjs(createdAt).format("YYYY-MM-DD")}
          </Text>
        </View>
      </View>
    </View>
  );
};

const BookingRequests: React.FC<BookingRequestsProps> = ({
  bookingRequests,
}) => {
  const sortedBookingRequests = bookingRequests.sort((a, b) => {
    return dayjs(a.event.date).diff(dayjs(b.event.date));
  });

  const getRequestsByStatus = (status: BookingRequestStatus) => {
    return sortedBookingRequests.filter(
      (bookingRequest) => bookingRequest.status === status,
    );
  };
  const pendingRequests = getRequestsByStatus("PENDING");
  const approvedRequests = getRequestsByStatus("APPROVED");
  const approvedNotSentRequests = getRequestsByStatus("APPROVED_NOT_SENT");
  const waitlistedRequests = getRequestsByStatus("WAITLISTED");
  const declinedRequests = getRequestsByStatus("DECLINED");
  const cancelledRequests = getRequestsByStatus("CANCELLED");

  const statuses = [
    "All",
    "Pending",
    "Approved",
    "Pending Delivery",
    "Waitlisted",
    "Declined",
    "Cancelled",
  ];
  const [selectedStatus, setSelectedStatus] = useState(statuses[0]);

  return (
    <View className="p-4">
      <ScrollView horizontal>
        <View className="flex flex-row gap-2">
          {statuses.map((status) => (
            <Chip
              key={status}
              onPress={() => setSelectedStatus(status)}
              selected={selectedStatus === status}
            >
              {status}
            </Chip>
          ))}
        </View>
      </ScrollView>
      {selectedStatus === "All" && (
        <View className="mt-4">
          {sortedBookingRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Pending" && (
        <View className="mt-4">
          {pendingRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Approved" && (
        <View className="mt-4">
          {approvedRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Pending Delivery" && (
        <View className="mt-4">
          {approvedNotSentRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Waitlisted" && (
        <View className="mt-4">
          {waitlistedRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Declined" && (
        <View className="mt-4">
          {declinedRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
      {selectedStatus === "Cancelled" && (
        <View className="mt-4">
          {cancelledRequests.map((bookingRequest) => (
            <BookingRequest
              key={bookingRequest.id}
              bookingRequest={bookingRequest}
            />
          ))}
        </View>
      )}
    </View>
  );
};

export default BookingRequests;
